export type TaskDateRange = { start: string; end: string };
export type TaskDatePreset = 'all' | 'today' | 'week' | 'month' | 'custom';

export const EMPTY_TASK_DATE_RANGE: TaskDateRange = Object.freeze({ start: '', end: '' });

const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'short' });
const formatMonth = (date: Date): string => monthFormatter.format(date).replace(/\.$/, '');
const formatDay = (date: Date): string => String(date.getDate()).padStart(2, '0');
const formatDisplayDate = (date: Date): string => (
  `${formatDay(date)} ${formatMonth(date)} ${String(date.getFullYear()).padStart(4, '0')}`
);

const formatCalendarDate = (date: Date): string => (
  `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const isValidCalendarDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

// O construtor numérico preserva o dia no fuso do navegador, inclusive durante DST.
const getLocalDay = (value: string, dayOffset = 0): Date => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day + dayOffset);
  // Date trata os anos 0–99 como 1900–1999; setFullYear corrige também a virada do ano.
  if (year < 100) date.setFullYear(year, month - 1, day + dayOffset);
  return date;
};

export const getTaskDatePreset = (
  preset: Exclude<TaskDatePreset, 'custom'>,
  now = new Date(),
): TaskDateRange => {
  if (preset === 'all') return { ...EMPTY_TASK_DATE_RANGE };

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') {
    const date = formatCalendarDate(today);
    return { start: date, end: date };
  }
  if (preset === 'week') {
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: formatCalendarDate(monday), end: formatCalendarDate(sunday) };
  }

  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { start: formatCalendarDate(firstDay), end: formatCalendarDate(lastDay) };
};

export const validateTaskDateRange = ({ start, end }: TaskDateRange): string | null => {
  if (start && !isValidCalendarDate(start)) return 'Informe uma data inicial válida.';
  if (end && !isValidCalendarDate(end)) return 'Informe uma data final válida.';
  if (start && end && start > end) return 'A data final deve ser igual ou posterior à data inicial.';
  return null;
};

/** Limite inicial inclusivo e final exclusivo: meia-noite local do dia seguinte. */
export const getTaskDateRangeParams = (range: TaskDateRange): {
  scheduled_from?: string;
  scheduled_until?: string;
} => {
  const error = validateTaskDateRange(range);
  if (error) throw new RangeError(error);
  return {
    ...(range.start ? { scheduled_from: getLocalDay(range.start).toISOString() } : {}),
    ...(range.end ? { scheduled_until: getLocalDay(range.end, 1).toISOString() } : {}),
  };
};

export const formatTaskDateRange = (range: TaskDateRange): string => {
  if (validateTaskDateRange(range)) return 'Período inválido';
  if (!range.start && !range.end) return 'Todas as datas';
  const displayDate = (value: string) => formatDisplayDate(getLocalDay(value));
  if (!range.end) return `A partir de ${displayDate(range.start)}`;
  if (!range.start) return `Até ${displayDate(range.end)}`;
  if (range.start === range.end) return displayDate(range.start);

  const start = getLocalDay(range.start);
  const end = getLocalDay(range.end);
  if (start.getFullYear() === end.getFullYear()) {
    if (start.getMonth() === end.getMonth()) return `${formatDay(start)}–${displayDate(range.end)}`;
    return `${formatDay(start)} ${formatMonth(start)} – ${displayDate(range.end)}`;
  }
  return `${displayDate(range.start)} – ${displayDate(range.end)}`;
};
